import { useMemo } from "react"
import { NUMERIC_OPERATORS } from "helpers/filters/filter-ons/numbers"
import { STRING_OPERATORS } from "helpers/filters/filter-ons/strings"
import { useModelCountries } from "resources/countries"
import { useModelCurrencies } from "resources/currencies"
import type { FilterOn } from "helpers/filters/filter-ons"
import { DataStatus, combineTanStackStatuses } from "helpers/data-statuses"

interface UseDescriptorFiltersParams {
  modelId: string
}

export function useDescriptorFilters({ modelId }: UseDescriptorFiltersParams): [FilterOn[], DataStatus] {
  const [countries, countriesStatus] = useModelCountries(modelId)
  const [currencies, currenciesStatus] = useModelCurrencies(modelId)

  const filters = useMemo(() => {
    if (!countries || !currencies) {
      return []
    }

    return [{
      label: "Descriptors",
      type: "descriptor" as const,
      options: [{
        label: "Country",
        type: "descriptor" as const,
        dataType: "string" as const,
        operators: STRING_OPERATORS,
        value: "country",
        options: countries,
      }, {
        label: "Currency",
        type: "descriptor" as const,
        dataType: "string" as const,
        operators: STRING_OPERATORS,
        value: "currency",
        options: currencies,
      }, {
        label: "Asset Class",
        type: "descriptor" as const,
        dataType: "string" as const,
        operators: STRING_OPERATORS,
        value: "assetClass",
        options: [{
          value: "commodity",
          label: "Commodity",
        }, {
          value: "currency",
          label: "Currency",
        }, {
          value: "equity",
          label: "Equity",
        }, {
          value: "fixedIncome",
          label: "Fixed Income",
        }, {
          value: "index",
          label: "Index",
        }, {
          value: "swap",
          label: "Basket",
        }],
      }, {
        label: "Asset Subclass",
        type: "descriptor" as const,
        dataType: "string" as const,
        operators: STRING_OPERATORS,
        value: "assetSubclass",
        options: [{
          value: "depositoryReceipt",
          label: "Depository Receipt",
        }, {
          value: "etf",
          label: "ETF",
        }, {
          value: "stock",
          label: "Stock",
        }],
      }, {
        label: "Average Daily Volume",
        type: "descriptor" as const,
        dataType: "millions" as const,
        operators: NUMERIC_OPERATORS,
        value: "averageDailyVolume",
      }, {
        label: "Market Cap",
        type: "descriptor" as const,
        dataType: "millions" as const,
        operators: NUMERIC_OPERATORS,
        value: "marketCapitalization",
      }],
    }]
  }, [countries, currencies])

  const status = combineTanStackStatuses([countriesStatus, currenciesStatus])

  return [filters, status]
}
